import { useState, useMemo, useEffect } from "react";
import { useForm, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "src/utils/useSnackbar";
import { useGetOneOrganization } from "src/api/client-api/company/useGetOneOrganization.ts";
import { useUpdateOrganization } from "src/api/client-api/company/useUpdateOrganization";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { CompanyGeneralFormValues, createCompanyGeneralFormSchema } from "../schemas/companyGeneralFormSchema";

export const useCompanyGeneralForm = () => {
    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const { selectedCompany } = useFoodpilotContext();
    const { isFeatureActivated } = useApplicationParameters();
    const isPaymentEnabled = isFeatureActivated("FS-payment-enabled");
    const companyId = selectedCompany?.id;

    const { company, isLoading: isCompanyLoading } = useGetOneOrganization({
        companyId: companyId?.toString(),
        withoutRelations: true,
    });

    const updateCompanyMutation = useUpdateOrganization();
    const isSaving = updateCompanyMutation.isLoading;

    const [isEditing, setIsEditing] = useState(false);

    const defaultValues: CompanyGeneralFormValues = useMemo(
        () => ({
            name: company?.name ?? "",
            email: company?.email ?? "",
            country: company?.country ?? "",
            zip: company?.zipcode ?? "",
            address1: company?.address1 ?? "",
            address2: company?.address2 ?? "",
            city: company?.city ?? "",
            state: company?.state ?? "",
            phone: company?.phone ?? "",
            vat: company?.registrationNumber ?? "",
        }),
        [company],
    );

    const schema = useMemo(() => createCompanyGeneralFormSchema(isPaymentEnabled, t), [isPaymentEnabled, t]);

    const resolver: Resolver<CompanyGeneralFormValues> = zodResolver(
        schema,
    ) as unknown as Resolver<CompanyGeneralFormValues>;

    const { control, handleSubmit, reset, formState, trigger } = useForm<CompanyGeneralFormValues>({
        resolver,
        defaultValues,
        mode: "onChange",
    });

    useEffect(() => {
        reset(defaultValues);
    }, [defaultValues, reset]);

    useEffect(() => {
        if (isEditing) {
            void trigger();
        }
    }, [isEditing, trigger]);

    const onSubmit = handleSubmit(async (values) => {
        const companyData = {
            name: values.name,
            email: values.email,
            country: values.country,
            zipcode: values.zip,
            address1: values.address1,
            address2: values.address2,
            city: values.city,
            state: values.state,
            phone: values.phone,
            registrationNumber: values.vat,
        };

        const filteredData = Object.fromEntries(
            Object.entries(companyData).filter(([_, value]) => value !== undefined),
        );

        try {
            if (!companyId) {
                throw new Error("No company ID found");
            }
            await updateCompanyMutation.mutateAsync({ companyId, data: filteredData });
            snackbar.setSnackbarProps({
                type: "success",
                message: t("companySettings.general.saved", "Settings updated"),
            });
            setIsEditing(false);
        } catch (error) {
            snackbar.setSnackbarProps({
                type: "error",
                message: t("companySettings.general.error", "Failed to update settings"),
            });
        }
    });

    return {
        control,
        formState,
        onSubmit,
        isEditing,
        setIsEditing,
        isCompanyLoading,
        isSaving,
        isPaymentEnabled,
        company,
        defaultValues,
        reset,
    };
};
